import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Card, CardContent, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      borderRadius: 8,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      border: "1px solid #e5e7eb",
    },
    content: {
      padding: theme.spacing(2),
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f2937",
    },
    tableWrap: {
      overflowX: "auto",
    },
    dataTable: {
      minWidth: 920,
    },
    headerCell: {
      background: "#f9fafb",
      color: "#6b7280",
      fontWeight: 700,
      whiteSpace: "nowrap",
      borderBottom: "1px solid #e5e7eb",
    },
    bodyCell: {
      whiteSpace: "nowrap",
      borderBottom: "1px solid #f1f5f9",
      color: "#334155",
    },
    assetName: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 700,
    },
    assetDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      display: "inline-block",
      flexShrink: 0,
    },
    loadingWrap: {
      minHeight: 120,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

const formatNumber = (value) => new Intl.NumberFormat("ja-JP").format(value);

const PlotAssetTable = ({ rows, colors, title, actionLabel, summaryRow, summaryLabel, loading }) => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <span className={classes.title}>{title}</span>
          {/* <Button variant="outlined" size="small">
            {actionLabel}
          </Button> */}
        </div>
        <div className={classes.tableWrap}>
          <Table className={classes.dataTable} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>アセット種別</TableCell>
                <TableCell className={classes.headerCell} align="right">プロット数</TableCell>
                <TableCell className={classes.headerCell} align="right">信頼度Lv1</TableCell>
                <TableCell className={classes.headerCell} align="right">信頼度Lv2</TableCell>
                <TableCell className={classes.headerCell} align="right">信頼度Lv3</TableCell>
                <TableCell className={classes.headerCell} align="right">信頼度Lv4</TableCell>
                <TableCell className={classes.headerCell} align="right">ユーザー数</TableCell>
                <TableCell className={classes.headerCell} align="right">1人あたり</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box className={classes.loadingWrap}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map((row, index) => (
                    <TableRow key={row.name} hover>
                      <TableCell className={classes.bodyCell} align="left">
                        <span className={classes.assetName}>
                          <span className={classes.assetDot} style={{ background: colors[index] || "#94a3b8" }} />
                          {row.name}
                        </span>
                      </TableCell>
                      <TableCell className={classes.bodyCell} align="right">{formatNumber(row.plotCount)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right">{formatNumber(row.lv1)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right">{formatNumber(row.lv2)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right">{formatNumber(row.lv3)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right">{formatNumber(row.lv4)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right">{formatNumber(row.users)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right">{row.perUser.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {summaryRow ? (
                    <TableRow>
                      <TableCell className={classes.bodyCell} style={{ fontWeight: 700 }}>{summaryLabel}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{formatNumber(summaryRow.plotCount)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{formatNumber(summaryRow.lv1)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{formatNumber(summaryRow.lv2)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{formatNumber(summaryRow.lv3)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{formatNumber(summaryRow.lv4)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{formatNumber(summaryRow.users)}</TableCell>
                      <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 700 }}>{summaryRow.perUser.toFixed(2)}</TableCell>
                    </TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

PlotAssetTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      plotCount: PropTypes.number.isRequired,
      lv1: PropTypes.number.isRequired,
      lv2: PropTypes.number.isRequired,
      lv3: PropTypes.number.isRequired,
      lv4: PropTypes.number.isRequired,
      users: PropTypes.number.isRequired,
      perUser: PropTypes.number.isRequired,
    })
  ).isRequired,
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string,
  actionLabel: PropTypes.string,
  summaryRow: PropTypes.shape({
    plotCount: PropTypes.number.isRequired,
    lv1: PropTypes.number.isRequired,
    lv2: PropTypes.number.isRequired,
    lv3: PropTypes.number.isRequired,
    lv4: PropTypes.number.isRequired,
    users: PropTypes.number.isRequired,
    perUser: PropTypes.number.isRequired,
  }),
  summaryLabel: PropTypes.string,
  loading: PropTypes.bool,
};

PlotAssetTable.defaultProps = {
  title: "アセット種別ごとのプロット数",
  actionLabel: "CSV出力",
  summaryRow: null,
  summaryLabel: "合計",
  loading: false,
};

export default PlotAssetTable;
